// Pure helpers for the team-scopes chip + drawer (S7). Split out from the
// components so the summary/grouping logic is unit-testable without RTL.

import { plural } from '../../lib/format.ts';
import type { MemberScope, MemberScopes, ScopeRef, ScopeType } from '../../api/org.ts';

/** Roster-chip label: "Org-wide", "No access", or "2 screens · 1 group". */
export function scopeSummary(scopes: MemberScopes): string {
  if (scopes === null) return 'Org-wide';
  const screens = scopes.filter((s) => s.scope_type === 'screen').length;
  const groups = scopes.filter((s) => s.scope_type === 'group').length;
  if (screens === 0 && groups === 0) return 'No access';
  const parts: string[] = [];
  if (screens) parts.push(`${screens} ${plural(screens, 'screen')}`);
  if (groups) parts.push(`${groups} ${plural(groups, 'group')}`);
  return parts.join(' · ');
}

/** Split a member's scopes into id sets per type, for seeding the picker's checkboxes. */
export function scopeIdsByType(scopes: MemberScopes): { screens: Set<string>; groups: Set<string> } {
  const screens = new Set<string>();
  const groups = new Set<string>();
  for (const s of scopes ?? []) {
    (s.scope_type === 'screen' ? screens : groups).add(s.scope_id);
  }
  return { screens, groups };
}

/** Build the `scopes` body for PUT /org/members/:id/scopes from the picker's selections. */
export function toScopeRefs(screenIds: Set<string>, groupIds: Set<string>): ScopeRef[] {
  const refs: ScopeRef[] = [];
  for (const id of screenIds) refs.push({ scope_type: 'screen', scope_id: id });
  for (const id of groupIds) refs.push({ scope_type: 'group', scope_id: id });
  return refs;
}

/** Enrich the picker's selections with display names for the optimistic update —
 *  looked up from whatever screens/groups the caller already has loaded. */
export function toOptimisticScopes(
  screenIds: Set<string>,
  groupIds: Set<string>,
  screenNames: Map<string, string>,
  groupNames: Map<string, string>,
): MemberScope[] {
  const out: MemberScope[] = [];
  for (const id of screenIds) {
    const name = screenNames.get(id);
    if (name) out.push({ scope_type: 'screen', scope_id: id, name });
  }
  for (const id of groupIds) {
    const name = groupNames.get(id);
    if (name) out.push({ scope_type: 'group', scope_id: id, name });
  }
  return out;
}

/** Sort helper for the searchable picker lists: selected items first, then A→Z. */
export function selectedFirst<T extends { id: string; name: string }>(
  items: T[],
  selected: Set<string>,
): T[] {
  return [...items].sort((a, b) => {
    const sa = selected.has(a.id) ? 0 : 1;
    const sb = selected.has(b.id) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name);
  });
}

export type { ScopeType };
